/**
 * Navigation utilities for handling internal links, anchor links, and external URLs
 */

/**
 * Check if a URL is relative (starts with / but not //)
 */
export function isRelativeUrl(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

/**
 * Check if a URL is external (different origin)
 */
export function isExternalUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Handle navigation to a URL with proper routing support
 */
export async function handleNavigation(href: string): Promise<void> {
  // Handle pure hash links (#anchor) - always scroll on current page
  if (href.startsWith('#')) {
    handleAnchorLink(href);
    return;
  }

  // Check if this is a hash-only navigation to current page (e.g., /#section while on /)
  const url = new URL(href, window.location.origin);
  const currentPath = window.location.pathname;

  // If the path matches current page and has a hash, just scroll to anchor
  if (url.pathname === currentPath && url.hash) {
    handleAnchorLink(url.hash);
    return;
  }

  // For relative URLs, try to use client-side navigation
  if (isRelativeUrl(href)) {
    try {
      // Dynamically import goto from SvelteKit if available
      const { goto } = await import('$app/navigation').catch(() => ({ goto: null }));
      if (goto) {
        await goto(href);
        return;
      }
    } catch {
      // Fall back to regular navigation if SvelteKit navigation is not available
    }
  }

  // Fall back to regular navigation for external URLs or when goto is not available
  window.location.href = href;
}

/**
 * Handle anchor link navigation with smooth scrolling
 */
export function handleAnchorLink(href: string): boolean {
  if (!href.startsWith('#')) {
    return false;
  }

  const sectionId = href.substring(1);
  const element = document.getElementById(sectionId);

  if (element) {
    // Blur any focused elements to remove focus outline
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Update URL hash without triggering navigation/reload
    // Use replaceState instead of pushState to avoid history pollution
    const currentPath = window.location.pathname;
    if (window.location.hash !== href) {
      history.replaceState(null, '', currentPath + href);
    }

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    return true;
  }

  return false;
}

/**
 * Comprehensive CTA click handler that supports all navigation types
 * Priority: href (especially anchors) > url > action
 */
export async function handleCtaNavigation(cta: {
  action?: string;
  url?: string;
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
}, onCtaClick?: (action?: string) => void): Promise<void> {
  // Handle href first - highest priority
  if (cta.href) {
    // Handle href navigation with target support
    // Let handleNavigation deal with all href formats (including #anchor and /#anchor)
    if (cta.target === '_blank') {
      window.open(cta.href, '_blank');
    } else {
      await handleNavigation(cta.href);
    }
    return;
  }

  // Handle url (external links) - second priority
  if (cta.url) {
    // Use target if specified, otherwise default behavior
    if (cta.target === '_blank' || (isExternalUrl(cta.url) && !cta.target)) {
      window.open(cta.url, '_blank');
    } else {
      await handleNavigation(cta.url);
    }
    return;
  }

  // Handle action-based CTAs last - lowest priority
  if (cta.action) {
    if (cta.action === 'backBrowser') {
      window.history.back();
    } else if (onCtaClick) {
      onCtaClick(cta.action);
    }
    return;
  }
}
